'use client';

function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import makeCancellable from 'make-cancellable-promise';
import makeEventProps from 'make-event-props';
import clsx from 'clsx';
import mergeRefs from 'merge-refs';
import invariant from 'tiny-invariant';
import warning from 'warning';
import PageContext from "./PageContext.js";
import Message from "./Message.js";
import PageCanvas from "./Page/PageCanvas.js";
import PageSVG from "./Page/PageSVG.js";
import TextLayer from "./Page/TextLayer.js";
import AnnotationLayer from "./Page/AnnotationLayer.js";
import { cancelRunningTask, isProvided, makePageCallback } from "./shared/utils.js";
import useDocumentContext from "./shared/hooks/useDocumentContext.js";
import useResolver from "./shared/hooks/useResolver.js";
import { eventProps, isClassName, isPageIndex, isPageNumber, isPdf, isRef, isRenderMode, isRotate } from "./shared/propTypes.js";
var defaultScale = 1;
/**
 * Displays a page.
 *
 * Should be placed inside `<Document />`. Alternatively, it can have `pdf` prop passed, which can be obtained from `<Document />`'s `onLoadSuccess` callback function, however some advanced functions like linking between pages inside a document may not be working correctly.
 */
var Page = function Page(props) {
  var _style;
  var documentContext = useDocumentContext();
  var mergedProps = Object.assign(Object.assign({}, documentContext), props);
  var _mergedProps$_classNa = mergedProps._className,
    _className = _mergedProps$_classNa === void 0 ? 'react-pdf__Page' : _mergedProps$_classNa,
    _mergedProps$_enableR = mergedProps._enableRegisterUnregisterPage,
    _enableRegisterUnregisterPage = _mergedProps$_enableR === void 0 ? true : _mergedProps$_enableR,
    canvasBackground = mergedProps.canvasBackground,
    canvasRef = mergedProps.canvasRef,
    children = mergedProps.children,
    className = mergedProps.className,
    CustomRenderer = mergedProps.customRenderer,
    customTextRenderer = mergedProps.customTextRenderer,
    devicePixelRatio = mergedProps.devicePixelRatio,
    _mergedProps$error = mergedProps.error,
    error = _mergedProps$error === void 0 ? 'Failed to load the page.' : _mergedProps$error,
    height = mergedProps.height,
    inputRef = mergedProps.inputRef,
    _mergedProps$loading = mergedProps.loading,
    loading = _mergedProps$loading === void 0 ? 'Loading page…' : _mergedProps$loading,
    _mergedProps$noData = mergedProps.noData,
    noData = _mergedProps$noData === void 0 ? 'No page specified.' : _mergedProps$noData,
    onGetAnnotationsErrorProps = mergedProps.onGetAnnotationsError,
    onGetAnnotationsSuccessProps = mergedProps.onGetAnnotationsSuccess,
    onGetStructTreeErrorProps = mergedProps.onGetStructTreeError,
    onGetStructTreeSuccessProps = mergedProps.onGetStructTreeSuccess,
    onGetTextErrorProps = mergedProps.onGetTextError,
    onGetTextSuccessProps = mergedProps.onGetTextSuccess,
    onLoadErrorProps = mergedProps.onLoadError,
    onLoadSuccessProps = mergedProps.onLoadSuccess,
    onRenderAnnotationLayerErrorProps = mergedProps.onRenderAnnotationLayerError,
    onRenderAnnotationLayerSuccessProps = mergedProps.onRenderAnnotationLayerSuccess,
    onRenderErrorProps = mergedProps.onRenderError,
    onRenderSuccessProps = mergedProps.onRenderSuccess,
    onRenderTextLayerErrorProps = mergedProps.onRenderTextLayerError,
    onRenderTextLayerSuccessProps = mergedProps.onRenderTextLayerSuccess,
    pageIndexProps = mergedProps.pageIndex,
    pageNumberProps = mergedProps.pageNumber,
    pdf = mergedProps.pdf,
    registerPage = mergedProps.registerPage,
    _mergedProps$renderAn = mergedProps.renderAnnotationLayer,
    renderAnnotationLayerProps = _mergedProps$renderAn === void 0 ? true : _mergedProps$renderAn,
    _mergedProps$renderFo = mergedProps.renderForms,
    renderForms = _mergedProps$renderFo === void 0 ? false : _mergedProps$renderFo,
    _mergedProps$renderMo = mergedProps.renderMode,
    renderMode = _mergedProps$renderMo === void 0 ? 'canvas' : _mergedProps$renderMo,
    _mergedProps$renderTe = mergedProps.renderTextLayer,
    renderTextLayerProps = _mergedProps$renderTe === void 0 ? true : _mergedProps$renderTe,
    rotateProps = mergedProps.rotate,
    _mergedProps$scale = mergedProps.scale,
    scaleProps = _mergedProps$scale === void 0 ? defaultScale : _mergedProps$scale,
    unregisterPage = mergedProps.unregisterPage,
    width = mergedProps.width,
    otherProps = __rest(mergedProps, ["_className", "_enableRegisterUnregisterPage", "canvasBackground", "canvasRef", "children", "className", "customRenderer", "customTextRenderer", "devicePixelRatio", "error", "height", "inputRef", "loading", "noData", "onGetAnnotationsError", "onGetAnnotationsSuccess", "onGetStructTreeError", "onGetStructTreeSuccess", "onGetTextError", "onGetTextSuccess", "onLoadError", "onLoadSuccess", "onRenderAnnotationLayerError", "onRenderAnnotationLayerSuccess", "onRenderError", "onRenderSuccess", "onRenderTextLayerError", "onRenderTextLayerSuccess", "pageIndex", "pageNumber", "pdf", "registerPage", "renderAnnotationLayer", "renderForms", "renderMode", "renderTextLayer", "rotate", "scale", "unregisterPage", "width"]);
  var _useResolver = useResolver(),
    _useResolver2 = _slicedToArray(_useResolver, 2),
    pageState = _useResolver2[0],
    pageDispatch = _useResolver2[1];
  var page = pageState.value,
    pageError = pageState.error;
  var pageElement = useRef(null);
  invariant(pdf, 'Attempted to load a page, but no document was specified. Wrap <Page /> in a <Document /> or pass explicit `pdf` prop.');
  var pageIndex = isProvided(pageNumberProps) ? pageNumberProps - 1 : pageIndexProps !== null && pageIndexProps !== void 0 ? pageIndexProps : null;
  var pageNumber = pageNumberProps !== null && pageNumberProps !== void 0 ? pageNumberProps : isProvided(pageIndexProps) ? pageIndexProps + 1 : null;
  var rotate = rotateProps !== null && rotateProps !== void 0 ? rotateProps : page ? page.rotate : null;
  var scale = useMemo(function () {
    if (!page) {
      return null;
    }
    // Be default, we'll render page at 100% * scale width.
    var pageScale = 1;
    // Passing scale explicitly null would cause the page not to render
    var scaleWithDefault = scaleProps !== null && scaleProps !== void 0 ? scaleProps : defaultScale;
    // If width/height is defined, calculate the scale of the page so it could be of desired width.
    if (width || height) {
      var viewport = page.getViewport({
        scale: 1,
        rotation: rotate
      });
      if (width) {
        pageScale = width / viewport.width;
      } else if (height) {
        pageScale = height / viewport.height;
      }
    }
    return scaleWithDefault * pageScale;
  }, [height, page, rotate, scaleProps, width]);
  function hook() {
    return function () {
      if (!isProvided(pageIndex)) {
        // Impossible, but TypeScript doesn't know that
        return;
      }
      if (_enableRegisterUnregisterPage && unregisterPage) {
        unregisterPage(pageIndex);
      }
    };
  }
  useEffect(hook, [_enableRegisterUnregisterPage, pdf, pageIndex, unregisterPage]);
  /**
   * Called when a page is loaded successfully
   */
  function onLoadSuccess() {
    if (onLoadSuccessProps) {
      if (!page || !scale) {
        // Impossible, but TypeScript doesn't know that
        return;
      }
      onLoadSuccessProps(makePageCallback(page, scale));
    }
    if (_enableRegisterUnregisterPage && registerPage) {
      if (!isProvided(pageIndex) || !pageElement.current) {
        // Impossible, but TypeScript doesn't know that
        return;
      }
      registerPage(pageIndex, pageElement.current);
    }
  }
  /**
   * Called when a page failed to load
   */
  function onLoadError() {
    if (!pageError) {
      // Impossible, but TypeScript doesn't know that
      return;
    }
    warning(false, pageError.toString());
    if (onLoadErrorProps) {
      onLoadErrorProps(pageError);
    }
  }
  function resetPage() {
    pageDispatch({
      type: 'RESET'
    });
  }
  useEffect(resetPage, [pageDispatch, pdf, pageIndex]);
  function loadPage() {
    if (!pdf || !pageNumber) {
      return;
    }
    var cancellable = makeCancellable(pdf.getPage(pageNumber));
    var runningTask = cancellable;
    cancellable.promise.then(function (nextPage) {
      pageDispatch({
        type: 'RESOLVE',
        value: nextPage
      });
    }).catch(function (error) {
      pageDispatch({
        type: 'REJECT',
        error: error
      });
    });
    return function () {
      return cancelRunningTask(runningTask);
    };
  }
  useEffect(loadPage, [pageDispatch, pdf, pageIndex, pageNumber, registerPage]);
  useEffect(function () {
    if (page === undefined) {
      return;
    }
    if (page === false) {
      onLoadError();
      return;
    }
    onLoadSuccess();
  },
  // Ommitted callbacks so they are not called every time they change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [page, scale]);
  var childContext = useMemo(function () {
    return (
      // Technically there cannot be page without pageIndex, pageNumber, rotate and scale, but TypeScript doesn't know that
      page && isProvided(pageIndex) && pageNumber && isProvided(rotate) && isProvided(scale) ? {
        _className: _className,
        canvasBackground: canvasBackground,
        customTextRenderer: customTextRenderer,
        devicePixelRatio: devicePixelRatio,
        onGetAnnotationsError: onGetAnnotationsErrorProps,
        onGetAnnotationsSuccess: onGetAnnotationsSuccessProps,
        onGetStructTreeError: onGetStructTreeErrorProps,
        onGetStructTreeSuccess: onGetStructTreeSuccessProps,
        onGetTextError: onGetTextErrorProps,
        onGetTextSuccess: onGetTextSuccessProps,
        onRenderAnnotationLayerError: onRenderAnnotationLayerErrorProps,
        onRenderAnnotationLayerSuccess: onRenderAnnotationLayerSuccessProps,
        onRenderError: onRenderErrorProps,
        onRenderSuccess: onRenderSuccessProps,
        onRenderTextLayerError: onRenderTextLayerErrorProps,
        onRenderTextLayerSuccess: onRenderTextLayerSuccessProps,
        page: page,
        pageIndex: pageIndex,
        pageNumber: pageNumber,
        renderForms: renderForms,
        renderTextLayer: renderTextLayerProps,
        rotate: rotate,
        scale: scale
      } : null
    );
  }, [_className, canvasBackground, customTextRenderer, devicePixelRatio, onGetAnnotationsErrorProps, onGetAnnotationsSuccessProps, onGetStructTreeErrorProps, onGetStructTreeSuccessProps, onGetTextErrorProps, onGetTextSuccessProps, onRenderAnnotationLayerErrorProps, onRenderAnnotationLayerSuccessProps, onRenderErrorProps, onRenderSuccessProps, onRenderTextLayerErrorProps, onRenderTextLayerSuccessProps, page, pageIndex, pageNumber, renderForms, renderTextLayerProps, rotate, scale]);
  var eventProps = useMemo(function () {
    return makeEventProps(otherProps, function () {
      return page ? scale ? makePageCallback(page, scale) : undefined : page;
    });
  }, [otherProps, page, scale]);
  var pageKey = "".concat(pageIndex, "@").concat(scale, "/").concat(rotate);
  var pageKeyNoScale = "".concat(pageIndex, "/").concat(rotate);
  function renderMainLayer() {
    switch (renderMode) {
      case 'custom':
        {
          invariant(CustomRenderer, "renderMode was set to \"custom\", but no customRenderer was passed.");
          return /*#__PURE__*/React.createElement(CustomRenderer, {
            key: "".concat(pageKey, "_custom")
          });
        }
      case 'none':
        return null;
      case 'svg':
        return /*#__PURE__*/React.createElement(PageSVG, {
          key: "".concat(pageKeyNoScale, "_svg")
        });
      case 'canvas':
      default:
        return /*#__PURE__*/React.createElement(PageCanvas, {
          key: "".concat(pageKey, "_canvas"),
          canvasRef: canvasRef
        });
    }
  }
  function renderTextLayer() {
    if (!renderTextLayerProps) {
      return null;
    }
    return /*#__PURE__*/React.createElement(TextLayer, {
      key: "".concat(pageKey, "_text")
    });
  }
  function renderAnnotationLayer() {
    if (!renderAnnotationLayerProps) {
      return null;
    }
    /**
     * As of now, PDF.js 2.0.943 returns warnings on unimplemented annotations in SVG mode.
     * Therefore, as a fallback, we render "traditional" AnnotationLayer component.
     */
    return /*#__PURE__*/React.createElement(AnnotationLayer, {
      key: "".concat(pageKey, "_annotations")
    });
  }
  function renderChildren() {
    return /*#__PURE__*/React.createElement(PageContext.Provider, {
      value: childContext
    }, renderMainLayer(), renderTextLayer(), renderAnnotationLayer(), children);
  }
  function renderContent() {
    if (!pageNumber) {
      return /*#__PURE__*/React.createElement(Message, {
        type: "no-data"
      }, typeof noData === 'function' ? noData() : noData);
    }
    if (pdf === null || page === undefined || page === null) {
      return /*#__PURE__*/React.createElement(Message, {
        type: "loading"
      }, typeof loading === 'function' ? loading() : loading);
    }
    if (pdf === false || page === false) {
      return /*#__PURE__*/React.createElement(Message, {
        type: "error"
      }, typeof error === 'function' ? error() : error);
    }
    return renderChildren();
  }
  return /*#__PURE__*/React.createElement("div", Object.assign({
    className: clsx(_className, className),
    "data-page-number": pageNumber,
    ref: mergeRefs(inputRef, pageElement),
    style: (_style = {}, _defineProperty(_style, '--scale-factor', "".concat(scale)), _defineProperty(_style, "backgroundColor", canvasBackground || 'white'), _defineProperty(_style, "position", 'relative'), _defineProperty(_style, "minWidth", 'min-content'), _defineProperty(_style, "minHeight", 'min-content'), _style)
  }, eventProps), renderContent());
};
var isFunctionOrNode = PropTypes.oneOfType([PropTypes.func, PropTypes.node]);
Page.propTypes = Object.assign(Object.assign({}, eventProps), {
  canvasBackground: PropTypes.string,
  canvasRef: isRef,
  children: PropTypes.node,
  className: isClassName,
  customRenderer: PropTypes.func,
  customTextRenderer: PropTypes.func,
  devicePixelRatio: PropTypes.number,
  error: isFunctionOrNode,
  height: PropTypes.number,
  imageResourcesPath: PropTypes.string,
  inputRef: isRef,
  loading: isFunctionOrNode,
  noData: isFunctionOrNode,
  onGetTextError: PropTypes.func,
  onGetTextSuccess: PropTypes.func,
  onLoadError: PropTypes.func,
  onLoadSuccess: PropTypes.func,
  onRenderError: PropTypes.func,
  onRenderSuccess: PropTypes.func,
  onRenderTextLayerError: PropTypes.func,
  onRenderTextLayerSuccess: PropTypes.func,
  pageIndex: isPageIndex,
  pageNumber: isPageNumber,
  pdf: isPdf,
  renderAnnotationLayer: PropTypes.bool,
  renderForms: PropTypes.bool,
  renderMode: isRenderMode,
  renderTextLayer: PropTypes.bool,
  rotate: isRotate,
  scale: PropTypes.number,
  width: PropTypes.number
});
export default Page;